import * as Yup from "yup"
import { toast, Bounce } from "react-toastify"
import { post, patch, del } from "../../../api/axios"
import { toastApiError } from "../../../helpers/apiError"
import endpoints from "../../../api/endpoints"

export const MAX_ONBOARDING_PROJECTS = 3

export const emptyOnboardingProject = (overrides = {}) => ({
  projectName: "",
  roleType: null,
  roleTitle: "",
  preferredCity: "",
  projectDescription: "",
  categories: [],
  startDate: "",
  endDate: "",
  isOngoing: false,
  ...overrides,
})

export const createProjectSchema = ({
  dateMode = "range",
  allowOngoing = true,
} = {}) => {
  const startDateSchema = Yup.string()
    .required(
      dateMode === "start-only"
        ? "Target date is required"
        : "Start date is required",
    )
    .test("before-end", "Start date must be before end date", function (start) {
      if (dateMode === "start-only") return true
      const { endDate, isOngoing } = this.parent
      if (!start || !endDate || (allowOngoing && isOngoing)) return true
      return start < endDate
    })

  const endAfterStartTest = function (end) {
    const { startDate } = this.parent
    if (!end || !startDate) return true
    return end > startDate
  }

  const endDateSchema =
    dateMode === "start-only"
      ? Yup.string().nullable()
      : allowOngoing
        ? Yup.string().when("isOngoing", {
            is: false,
            then: schema =>
              schema
                .required("End date is required")
                .test(
                  "after-start",
                  "End date must be after start date",
                  endAfterStartTest,
                ),
            otherwise: schema => schema.nullable(),
          })
        : Yup.string()
            .required("End date is required")
            .test(
              "after-start",
              "End date must be after start date",
              endAfterStartTest,
            )

  return Yup.object({
    projectName: Yup.string().trim().required("Project name is required"),
    roleType: Yup.object().nullable().required("Role is required"),
    preferredCity: Yup.string().required("Preferred city is required"),
    projectDescription: Yup.string()
      .trim()
      .required("Project description is required"),
    categories: Yup.array().min(1, "Select at least one category"),
    startDate: startDateSchema,
    endDate: endDateSchema,
  })
}

export const toProjectPayloadItem = (project, onboardingStage) => ({
  ...(project.id ? { id: project.id } : {}),
  name: project.projectName.trim(),
  description: project.projectDescription.trim(),
  start_date: project.startDate,
  end_date: project.isOngoing ? null : project.endDate || null,
  is_ongoing: !!project.isOngoing,
  category_ids: [...(project.categories || [])],
  project_cities: project.preferredCity
    ? [{ city_id: project.preferredCity }]
    : [],
  creator_member: {
    role_type_ids: project.roleType?.id ? [project.roleType.id] : [],
  },
  onboarding: true,
  onboarding_stage: onboardingStage,
})

export const applyFormErrors = (formik, errors) => {
  formik.setErrors(errors)
  formik.setTouched(
    Object.keys(errors).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {}),
    false,
  )
}

const onboardingProjectFieldIsFilled = (field, values = {}) => {
  switch (field) {
    case "projectName":
    case "projectDescription":
      return String(values[field] || "").trim().length > 0
    case "roleType":
    case "roleTitle":
      return Boolean(values.roleType)
    case "preferredCity":
      return Boolean(values.preferredCity)
    case "categories":
      return (values.categories || []).length > 0
    case "startDate":
      return Boolean(values.startDate)
    case "endDate":
      return Boolean(values.endDate) || Boolean(values.isOngoing)
    default:
      return false
  }
}

const clearOnboardingProjectFieldErrors = (formik, field) => {
  const clear = name => {
    if (formik.errors?.[name]) {
      formik.setFieldError(name, undefined)
    }
  }

  if (field === "startDate" || field === "endDate") {
    clear("startDate")
    clear("endDate")
    return
  }

  if (field === "roleType" || field === "roleTitle") {
    clear("roleType")
    clear("roleTitle")
    return
  }

  clear(field)
}

export const createOnboardingProjectFieldHandlers = formik => {
  const setFieldValue = (field, value) => {
    formik.setFieldValue(field, value)
    clearOnboardingProjectFieldErrors(formik, field)
  }

  const clearFieldErrorOnBlur = field => {
    if (!onboardingProjectFieldIsFilled(field, formik.values)) return
    clearOnboardingProjectFieldErrors(formik, field)
  }

  return { setFieldValue, clearFieldErrorOnBlur }
}

export const projectDraftHasContent = values =>
  values.projectName.trim().length > 0 ||
  !!values.roleType ||
  !!values.preferredCity ||
  values.projectDescription.trim().length > 0 ||
  (values.categories || []).length > 0 ||
  !!values.startDate ||
  !!values.endDate

export const collectOnboardingProjects = async ({
  items,
  formik,
  editingIndex,
}) => {
  let final = [...items]

  if (editingIndex !== null) {
    const errors = await formik.validateForm(formik.values)
    if (Object.keys(errors).length > 0) {
      applyFormErrors(formik, errors)
      return null
    }
    final[editingIndex] = formik.values
    return final
  }

  if (projectDraftHasContent(formik.values)) {
    const errors = await formik.validateForm(formik.values)
    if (Object.keys(errors).length > 0) {
      applyFormErrors(formik, errors)
      return null
    }
    final = [...final, formik.values]
  }

  return final
}

export const submitOnboardingProjects = async ({
  projects,
  onboardingStage,
  onSuccess,
}) => {
  if (!projects.length) {
    onSuccess?.([])
    return true
  }

  const newProjects = projects.filter(project => !project.id)

  if (!newProjects.length) {
    onSuccess?.(projects)
    return true
  }

  const payload = {
    projects: newProjects.map(project =>
      toProjectPayloadItem(project, onboardingStage),
    ),
  }
  const res = await post(endpoints.createProfileBatch, payload)

  if (res?.data?.success) {
    toast.success(res?.data?.message || "Projects saved successfully", {
      position: "bottom-center",
      theme: "colored",
      transition: Bounce,
    })

    const created = Array.isArray(res?.data?.result)
      ? res.data.result
      : [res?.data?.result].filter(Boolean)
    const createdByProject = new Map()
    newProjects.forEach((project, i) =>
      createdByProject.set(project, created[i]),
    )

    const nextProjects = projects.map(project =>
      project.id || !createdByProject.has(project)
        ? project
        : { ...project, id: createdByProject.get(project)?.id },
    )

    onSuccess?.(nextProjects)
    return true
  }

  toastApiError(res, "Failed to save projects")
  return false
}

export const updateOnboardingProject = async ({ project, onboardingStage }) => {
  if (!project?.id) return false

  try {
    const { id, onboarding, onboarding_stage, ...payload } =
      toProjectPayloadItem(project, onboardingStage)
    const res = await patch(endpoints.updateProject(project.id), payload)

    if (res?.data?.success) {
      toast.success(res?.data?.message || "Project updated", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return true
    }

    toastApiError(res, "Failed to update project")
    return false
  } catch (error) {
    toastApiError(error, "Failed to update project")
    return false
  }
}

export const deleteOnboardingProject = async projectId => {
  if (!projectId) return false

  try {
    const res = await del(endpoints.deleteProject(projectId))

    if (res?.data?.success) {
      toast.success(res?.data?.message || "Project deleted", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return true
    }

    toastApiError(res, "Failed to delete project")
    return false
  } catch (error) {
    toastApiError(error, "Failed to delete project")
    return false
  }
}
